import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import AdminHealthBanner, { AdminHealthPill } from "./AdminHealthBanner";
import { LayoutDashboard, Sparkles, Image as ImageIcon, LogOut, Users, ScrollText, KeyRound, Megaphone, Store, BarChart3, Wallet, Phone } from "lucide-react";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState({ services: 0, portfolio: 0 });

  useEffect(() => {
    api.get("/pending-counts").then((r) => setPending(r.data)).catch(() => {});
  }, [location.pathname]);

  const isAdmin = user?.role === "admin";

  const navItems = [
    { to: "/admin", label: "Inquiries", icon: LayoutDashboard, end: true, testId: "admin-nav-inquiries" },
    { to: "/admin/services", label: "Services", icon: Sparkles, testId: "admin-nav-services", badge: pending.services },
    { to: "/admin/portfolio", label: "Portfolio", icon: ImageIcon, testId: "admin-nav-portfolio", badge: pending.portfolio },
    { to: "/admin/ads", label: "Advertisements", icon: Megaphone, testId: "admin-nav-ads" },
    ...(isAdmin ? [
      { to: "/admin/restaurants", label: "Restaurants", icon: Store, testId: "admin-nav-restaurants" },
      { to: "/admin/payments", label: "Payments", icon: Wallet, testId: "admin-nav-payments" },
      { to: "/admin/site-stats", label: "Home stats", icon: BarChart3, testId: "admin-nav-site-stats" },
      { to: "/admin/site-contact", label: "Contact info", icon: Phone, testId: "admin-nav-site-contact" },
      { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users" },
      { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, testId: "admin-nav-audit" },
    ] : []),
    { to: "/admin/change-password", label: "Password", icon: KeyRound, testId: "admin-nav-pwd" },
  ];

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#FAFAFA] flex">
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-black/10 sticky top-0 h-screen">
        <div className="p-6 border-b border-black/10">
          <div className="font-display font-black text-xl">Restro<span className="text-[var(--brand-accent)]">Setup</span></div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <div className="text-xs uppercase tracking-[0.2em] text-[#525252]">{isAdmin ? "Admin" : "Editor"}</div>
            <AdminHealthPill />
          </div>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId}
                     className={({ isActive }) =>
                       `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                         isActive ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A] hover:bg-black/5"}`}>
              <n.icon size={18} strokeWidth={1.75} /> <span className="flex-1">{n.label}</span>
              {!!n.badge && <span data-testid={`${n.testId}-badge`} className="ml-auto bg-[var(--brand-primary)] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{n.badge}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-black/10">
          <div className="text-xs text-[#525252] mb-2">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email || user?.mobile}</div>
          <button onClick={handleLogout} data-testid="admin-logout-button"
                  className="mt-3 w-full flex items-center justify-center gap-2 text-sm bg-[#FAFAFA] hover:bg-black/5 border border-black/10 px-3 py-2 rounded-sm">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-black/10 px-4 h-14 flex items-center justify-between">
        <div className="font-display font-black">Restro<span className="text-[var(--brand-accent)]">Setup</span></div>
        <button onClick={handleLogout} className="text-sm flex items-center gap-1" data-testid="admin-logout-mobile"><LogOut size={16} /> Logout</button>
      </div>

      <main className="flex-1 pt-14 md:pt-0">
        <AdminHealthBanner />
        <div className="md:hidden flex overflow-x-auto bg-white border-b border-black/10 px-2">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
                     className={({ isActive }) => `px-4 py-3 text-sm whitespace-nowrap relative ${isActive ? "border-b-2 border-[var(--brand-primary)] text-[#0A0A0A]" : "text-[#525252]"}`}>
              {n.label}{!!n.badge && <span className="ml-1.5 bg-[var(--brand-primary)] text-white text-[10px] font-bold rounded-full px-1.5">{n.badge}</span>}
            </NavLink>
          ))}
        </div>
        <div key={location.pathname} className="page-transition"><Outlet /></div>
      </main>
    </div>
  );
}
